import { create } from 'zustand'
import { devtools } from 'zustand/middleware'
import http from '@lib/http-client'
import { toUtcIsoString } from '@utils/date'
import {
  IRemoveNeedsAttentionResponse,
  IUserActivitiesPage,
  IUserContactInput,
  IUserInfo,
  IUserProfile,
  IUserSettings,
  UserStore
} from './types'

// the endpoint documents contacts as plain strings but returns objects
// shaped like { value, validated, legacy }; accept both
const contactValue = (raw: unknown): string | undefined => {
  if (typeof raw === 'string') {
    return raw
  }
  const value = (raw as { value?: unknown })?.value
  return typeof value === 'string' ? value : undefined
}

export const useUserStore = create<UserStore>()(
  devtools(() => ({
    getUserInfo: async (userId: number) => {
      try {
        const response = await http.get(`/user/${userId}/info`)

        if (!response?.id) {
          return null
        }

        const info = response as IUserInfo
        return {
          ...info,
          created: toUtcIsoString(info.created) ?? info.created,
          contacts: {
            phone: contactValue(info.contacts?.phone),
            email: contactValue(info.contacts?.email)
          }
        }
      } catch (error) {
        console.error('Error fetching user info:', error)
        return null
      }
    },

    // secondary contacts only: the MAIN phone and email come from getUserInfo
    getUserProfile: async (userId: number) => {
      try {
        const response = await http.get(`/user/${userId}/profile`)

        if (!response) {
          return null
        }

        const profile = response as IUserProfile
        return {
          ...profile,
          phones: (profile.phones ?? [])
            .map(entry => ({ ...entry, phone: contactValue(entry.phone) }))
            .filter(entry => entry.phone) as IUserProfile['phones'],
          emails: (profile.emails ?? [])
            .map(entry => ({ ...entry, email: contactValue(entry.email) }))
            .filter(entry => entry.email) as IUserProfile['emails']
        }
      } catch (error) {
        console.error('Error fetching user profile:', error)
        return null
      }
    },

    getUserSettings: async (userId: number) => {
      try {
        const response = await http.get(`/user/${userId}/settings`)

        return (response as IUserSettings) ?? null
      } catch (error) {
        console.error('Error fetching user settings:', error)
        return null
      }
    },

    rematchMember: async (professionalId: number, memberId: number) => {
      try {
        await http.post(
          `/rematch/professional/${professionalId}/member/${memberId}`
        )

        return true
      } catch (error) {
        console.error('Error rematching member:', error)
        return false
      }
    },

    removeMemberNeedsAttention: async (userId: number) => {
      try {
        const response = await http.post(
          `/user/${userId}/remove_member_needs_attention`
        )

        return (response ?? {}) as IRemoveNeedsAttentionResponse
      } catch (error) {
        console.error('Error removing member needs attention:', error)
        return null
      }
    },

    addUserEmail: async (userId: number, email: IUserContactInput) => {
      try {
        await http.post(`/professional/user/${userId}/emails`, { ...email })

        return true
      } catch (error) {
        console.error('Error adding user email:', error)
        return false
      }
    },

    getUserActivities: async ({
      userId,
      eventType,
      page = 0,
      pageSize = 7
    }: {
      userId: number
      eventType?: string
      page?: number
      pageSize?: number
    }) => {
      try {
        const response = await http.get(
          `/user/${userId}/activities?event_type=${eventType}&page=${page}&page_size=${pageSize}`
        )

        return (response as IUserActivitiesPage) ?? null
      } catch (error) {
        console.error('Error fetching user activities:', error)
        return null
      }
    },

    addUserPhone: async (userId: number, phone: IUserContactInput) => {
      try {
        await http.post(`/professional/user/${userId}/phones`, { ...phone })

        return true
      } catch (error) {
        console.error('Error adding user phone:', error)
        return false
      }
    }
  }))
)
